/**
 * The transport.
 *
 * This is the one place in the package where `fetch` is called. Everything above it (the REST
 * operations, the tile helpers, the Data API wrappers) composes this function, and
 * `@mountaya/elements` never reaches it at all, which is where the boundary falls.
 */

import {
  assertCredential,
  type Credential,
  credentialHeaders,
  holdsSession,
  isPublishableCredential,
  isSecretCredential,
  isShareCredential,
  type OperationCredentials,
  sessionExpiryOf,
  sessionSourceOf,
  sessionTokenOf,
  sessionUserOf,
  wantsCookies,
} from "./auth.js";
import { type DateMask, decodeDates } from "./dates.js";
import {
  answerDetailsOf,
  describeType,
  entriesOf,
  MountayaConfigurationError,
  MountayaConnectionError,
  parseRetryAfter,
  refuseOption,
  replayedOf,
} from "./errors.js";
import { isEmptyHeaderValue, isHeaderValue } from "./headerValue.js";
import { mintIdempotencyKey } from "./idempotencyKey.js";
import type { RateLimitInfo } from "./rateLimit.js";
import {
  isAborted,
  parseSuccessBody,
  readBytes,
  readFailure,
  readFailureText,
  readText,
} from "./responseBody.js";
import {
  abortReasonOf,
  backoffDelay,
  defaultRetry,
  isKeyInUse,
  jitteredDelay,
  KEY_IN_USE_MIN_WAIT_MS,
  MAX_TIMER_DELAY_MS,
  namedError,
  type RetryOptions,
  resolveRetry,
  type Surface,
  shouldRetry,
  sleep,
} from "./retry.js";
import { waitForSessionToken } from "./sessionWait.js";

/**
 * Default base URLs, one per surface.
 *
 * `tiles` and `rest` are each the `servers` entry of their own public bundle, so each moves
 * only when the contract does. `data` has no published server URL to read (the Data API is
 * reached through a URL set per environment), so it is stated here.
 *
 * `scripts/contract-checkout.test.mjs` compares every entry against the contract checkout beside
 * the repository, which is what keeps this object derived rather than hand-kept: a default that
 * drifts from the published server URL is a first call that reaches the wrong host, and nothing
 * else in the toolchain would notice.
 */
export const defaultBaseUrls: Readonly<Record<Surface, string>> = {
  /** The Data API, stated here since the Data API publishes no server URL to read. */
  data: "https://data.mountaya.com",
  /** The Map Studio REST API, where the event streams are opened too. */
  rest: "https://api.mountaya.com",
  /** The Tile API. */
  tiles: "https://tiles.mountaya.com",
};

/**
 * DEFAULT_TIMEOUT_MS is how long one attempt waits for a response's headers when a client names
 * no `timeoutMs`.
 */
const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * SERVER_BUDGET_MARGIN_MS is how much longer than an operation's own `x-timeout-ms` an attempt
 * waits by default, in milliseconds: the API answers a 504 itself once it has spent that long, and
 * the margin lets that answer arrive before the client gives up on it, so a write the API is
 * still running is not abandoned for a repeat that would meet it in flight.
 */
const SERVER_BUDGET_MARGIN_MS = 5000;

/**
 * ResponseReport is what {@link ClientOptions.onResponse} is told of each attempt an answer came
 * back to: which call, on which surface, which attempt, the answer's status, whether the API gave
 * it again for a key it had seen, the id the API recorded it under and the rate-limit state it
 * describes.
 */
export interface ResponseReport {
  /** Which attempt of the call the answer is to, 0 for the first. */
  readonly attempt: number;
  /** The contract's name for the call's operation. */
  readonly operationId: string;
  /**
   * The rate-limit state the answer's `RateLimit-Policy` and `RateLimit` describe, where they
   * describe one: the Data API and the Tile API do on every answer made under a key.
   */
  readonly rateLimit?: RateLimitInfo | undefined;
  /**
   * Whether the answer carries `Idempotent-Replayed: true`: the API kept it for the call's
   * `Idempotency-Key`, which an earlier request carried, and gave it again rather than run the
   * write a second time.
   */
  readonly replayed: boolean;
  /** The answer's `X-Request-Id`, exactly as sent, where it sent one. */
  readonly requestId?: string | undefined;
  /** The answer's HTTP status. */
  readonly status: number;
  /** The API the call went to. */
  readonly surface: Surface;
}

/**
 * BaseUrlOverrides names the base URL a client reaches each surface at, one surface at a time:
 * what {@link ClientOptions.baseUrls} takes.
 *
 * A surface left out keeps its default, and so does one written as `undefined`, which a caller
 * forwarding a URL it may not hold writes. Each member states that `undefined`, so the same
 * forward compiles for a consumer under `exactOptionalPropertyTypes`.
 */
export interface BaseUrlOverrides {
  /**
   * The Data API's base URL.
   *
   * @default "https://data.mountaya.com"
   */
  readonly data?: string | undefined;
  /**
   * The Map Studio REST API's base URL, which the event streams are opened on too.
   *
   * @default "https://api.mountaya.com"
   */
  readonly rest?: string | undefined;
  /**
   * The Tile API's base URL.
   *
   * @default "https://tiles.mountaya.com"
   */
  readonly tiles?: string | undefined;
}

/**
 * ClientOptions configures a client. Only the credential is required: a first call should not
 * need a base URL, a timeout policy or a retry policy to be chosen.
 *
 * `timeoutMs`, each `retry` member, each `baseUrls` entry, `language` and `fetch` are checked at
 * construction, and one that cannot work throws a `MountayaConfigurationError` naming the option
 * and the value. A credential that cannot work throws one naming what is wrong with it: an empty
 * or blank key reads as a missing credential, nothing of a share token, a session token or a
 * value in the secret key's slot is quoted, and of a value in the publishable key's slot under
 * the wrong prefix its first three characters alone, which tell which value landed there. A
 * refused `baseUrls` entry that holds an `@` is named without its value, since a password may
 * stand before the `@`, and options that are not an object at all are named by their type. A
 * member written as `undefined` is an omitted one, so a caller forwarding an optional value
 * keeps the default.
 */
export interface ClientOptions {
  /** How the client authenticates: see {@link Credential} for the shapes it takes. */
  readonly credential: Credential;
  /**
   * Overrides the default base URL per surface, one surface at a time. Useful against a local
   * stack or behind a gateway.
   *
   * Each entry is an absolute `http:` or `https:` URL with no query and no fragment
   * (`http://localhost:8080`), a path starting with one `/` for a gateway on the page's own
   * origin (`/api`), or `""` for the page's own origin itself. A bare host such as
   * `api.example.com` is refused rather than joined as a path, and so is a path whose dot
   * segments or backslashes resolve it to `//host`, which `fetch` reads as another origin, and an
   * entry with whitespace at either end. An absolute URL names no username and no password
   * (`https://user:pass@host`), which `fetch` refuses in a request's URL.
   *
   * The page's own origin is the origin of the page's address, `location`, which a `<base>`
   * element does not move: a request under a path or `""` leaves as an absolute URL at that
   * origin, never as a relative one the document's base URL could send to another host. Where
   * no page names an `http` or `https` origin (Node, `about:blank`, a `file:` document), the
   * path is handed to `fetch` as written.
   *
   * @default {@link defaultBaseUrls}, entry by entry: a surface left out keeps its own.
   */
  readonly baseUrls?: BaseUrlOverrides | undefined;
  /**
   * How long one attempt waits for a response to arrive, in whole milliseconds from 1 to
   * 2,147,483,647.
   *
   * It bounds each attempt rather than the retry sequence, so a retried call is not left with
   * nothing by the attempt before it. An operation that states the most the API spends on it
   * (`x-timeout-ms`) waits that plus five seconds where that is longer, so an import is not
   * abandoned while the API is still running it; a call's own `timeoutMs` stands over both. It stops once the response's headers are in: a large body
   * still arriving is not cut off, and only the caller's `signal` ends it. An attempt that
   * reaches it fails as a `MountayaConnectionError` saying so, and is retried as a network
   * failure is.
   *
   * A refusal's body is the one read with a bound of its own, whatever this is set to: an error
   * envelope is read from at most 64 KiB arriving within 5 seconds, and past either the status
   * alone classifies the failure.
   *
   * @default 30000
   */
  readonly timeoutMs?: number | undefined;
  /** The retry policy, laid over the defaults member by member: see {@link RetryOptions}. */
  readonly retry?: RetryOptions | undefined;
  /**
   * Told of every attempt an answer came back to, once each, in order: a success, a refusal and
   * an answer the client repeats alike, the open of an event stream included. An attempt that
   * never arrived has no answer to report. See {@link ResponseReport} for what it is told.
   *
   * For observing, never for steering: the client delays no request on the rate-limit state it
   * reports, and a fault the hook throws changes nothing the call answers. It reaches the
   * runtime as the program's own uncaught error, on a microtask. A value that is not a function
   * is refused at construction.
   */
  readonly onResponse?: ((report: ResponseReport) => void) | undefined;
  /**
   * BCP 47 tag sent as `Accept-Language` (`"fr"`, `"pt-BR"`). A value that is not a string, one
   * no request header carries (a line break inside it, a character outside Latin-1) and one that
   * is empty or whitespace alone are each refused at construction.
   *
   * Worth setting. The API localizes its validation sentences, and applications
   * splice those into copy they have already translated: leaving it unset yields a
   * reason in whatever language the runtime happens to prefer, and a user reads one
   * message written half in each.
   *
   * @default no `Accept-Language` is sent.
   */
  readonly language?: string | undefined;
  /**
   * Replaces the global fetch. For tests, proxies and instrumentation. A value that is not a
   * function is refused at construction.
   *
   * A function that reads the response it answers reads a clone of it. A body already read, or
   * one a reader is still held on, cannot be read a second time: a success is then reported as a
   * body that broke off, a `MountayaConnectionError`, and a failure is classified by its status
   * alone, with no entry.
   *
   * @default the global `fetch`, read at each call.
   */
  readonly fetch?: typeof fetch | undefined;
}

/**
 * RequestOptions are the per-call knobs a CALLER sets, as the last parameter of every generated
 * operation and of every bound method.
 *
 * The distinction from {@link RequestArgs} is who builds it: an operation builds those from the
 * contract (a path, a method, an idempotence flag), and a caller has no business stating any of
 * them. This is the other half, and it is deliberately narrow: everything a request needs that a
 * generated signature cannot know.
 *
 * The Data API's options bag extends this one, so a knob added here reaches all three surfaces.
 */
export interface RequestOptions {
  /**
   * Overrides the client's retry policy for this call alone.
   *
   * Merged over the client's own, member by member, so a call that sets only `maxRetries` keeps
   * the client's delays. The policy is the contract's to state per client AND per call: a
   * search box that re-queries on every keystroke wants no retries at all, and a background
   * sync on the same client wants the defaults, and neither should need a second client to say
   * so. Which statuses are repeated is not a knob: that is the contract's rule, not the
   * caller's. Checked before the first attempt, as a client's is at construction.
   */
  readonly retry?: RetryOptions | undefined;
  /**
   * How long each attempt of this call waits for a response, in whole milliseconds from 1 to
   * 2,147,483,647, in place of the client's {@link ClientOptions.timeoutMs} and of the budget
   * the operation states, longer or shorter than either. It bounds a wait for the first session
   * token as well. A value outside the range is refused before the first attempt, naming the
   * call.
   *
   * @default the larger of the client's own `timeoutMs` and the operation's `x-timeout-ms` plus
   *   five seconds, where it states one.
   */
  readonly timeoutMs?: number | undefined;
  /**
   * Aborts the request, any retry of it, and the reading of its body.
   *
   * Joined to each attempt's own timeout, so a caller's abort and a timeout are both honoured
   * rather than the last one set winning, and it settles a call at once wherever the call is:
   * waiting for headers, waiting out a backoff, or reading a body, a failure's as well as a
   * success's. The call rejects with the signal's own reason, never rewrapped, or with an
   * `AbortError` where the signal carries none. A body still arriving is cancelled, which lets
   * go of its connection, on every runtime that streams a response's body. Where the runtime
   * buffers bodies whole and exposes no stream (React Native), the read is abandoned instead.
   */
  readonly signal?: AbortSignal | undefined;
}

/**
 * RequestArgs describes one call: what a generated operation builds, and what a consumer builds
 * for an operation this client does not bind, stating what that operation declares (`dates`,
 * `requiresSecretKey`, `takesNoCredential`, `acceptsShareToken`, `requiresSession`,
 * `actsAsMember`, `serverBudgetMs`, `takesIdempotencyKey`).
 *
 * A declaration left out is one the operation does not make, so a hand-built call states each
 * one its operation makes: without `dates` its body decodes no `Date`, and without
 * `requiresSecretKey` or `takesNoCredential` a secret key is refused on it. No declaration
 * refuses a cookie credential, which is outside the published contract.
 */
export interface RequestArgs {
  /** The API the call goes to, whose base URL the path is joined onto. */
  readonly surface: Surface;
  /** The HTTP method, as the contract states it (`"GET"`, `"POST"`). */
  readonly method: string;
  /**
   * The path under the surface's base URL, starting with `/`, each parameter already
   * percent-encoded. A path that leaves the base URL, or holds an empty or a dot segment, is
   * refused before the first attempt.
   */
  readonly path: string;
  /** The contract's name for the operation, which every refusal and failure message names. */
  readonly operationId: string;
  /**
   * The instants in the success body, as a {@link DateMask}: each string the mask names decodes
   * to a `Date` where it is a full ISO 8601 timestamp carrying a zone and naming a real instant,
   * and every other string stays a string. A generated operation states the mask its contract
   * declares, and nothing where the contract declares no instant. Omitted, the body decodes no
   * `Date` at all.
   */
  readonly dates?: DateMask | undefined;
  /**
   * Whether the operation is authenticated by a secret key: the contract declares
   * `x-pkey-scope: none` beside a security requirement, which `postSession` alone does. A
   * generated operation states `true` there and nothing elsewhere. Where it is stated, every
   * credential but a secret key is refused locally. Where it is not, a secret key is refused,
   * unless the operation takes no credential: the API answers a secret key on an operation it
   * does not authenticate with a 401, as if nothing had been sent.
   */
  readonly requiresSecretKey?: boolean | undefined;
  /**
   * Whether the operation takes no credential at all: its `security` is an empty list, which
   * `postOrganizationLookup`'s is. A generated operation states `true` there and nothing
   * elsewhere. Where it is stated, the request carries nothing the client's credential holds,
   * no header and no cookie, whatever origin the API is reached at, and no credential is
   * refused.
   */
  readonly takesNoCredential?: boolean | undefined;
  /**
   * Whether one of the operation's security requirements is a share token on its own. A
   * generated operation states `true` where the contract does and nothing elsewhere, and
   * nothing means no: a share token is refused locally, before the first attempt, on every
   * operation that does not state it. That is how a share-token write is refused, and how a read
   * no share link reaches is refused before the 401 the server would answer.
   *
   * A client holding a share token beside a key sends the share token alone where this is
   * `true`, since a share token REPLACES the key: the server, sent both, would read the key.
   */
  readonly acceptsShareToken?: boolean | undefined;
  /**
   * Whether every one of the operation's security requirements names a session token. A
   * generated operation states `true` where the contract does, and a credential carrying none (a
   * bare publishable key, a secret key, a share token) is then refused locally rather than sent
   * to be answered with a 401. A credential whose {@link SessionTokenSource} has a token on its
   * way waits for it first, and is refused only once that wait ends with none. The contract
   * states it on every `management` and every `studio-embedded` operation, and the Data API on
   * every operation.
   */
  readonly requiresSession?: boolean | undefined;
  /**
   * The caller's per-call override of the client's retry policy, from
   * {@link RequestOptions.retry}. Merged over the client's policy member by member.
   */
  readonly retry?: RetryOptions | undefined;
  /**
   * How long each attempt of this call waits for a response, from {@link RequestOptions.timeoutMs}:
   * a whole number of milliseconds from 1 to 2,147,483,647, which stands over the client's own
   * and over {@link RequestArgs.serverBudgetMs}. A value outside the range is refused before the
   * first attempt, naming the call.
   *
   * @default the larger of the client's own `timeoutMs` and `serverBudgetMs` plus five seconds.
   */
  readonly timeoutMs?: number | undefined;
  /**
   * The most the API spends on the operation before it answers a 504 itself, its `x-timeout-ms`,
   * in whole milliseconds. A generated operation states it wherever the contract does, which is
   * every operation but an event stream. Each attempt then waits the larger of the client's own
   * `timeoutMs` and this plus five seconds, unless the call names its own.
   */
  readonly serverBudgetMs?: number | undefined;
  /**
   * Whether repeating this request is safe. Declared rather than inferred: the Data API
   * is GraphQL over POST and every one of its operations is a read.
   */
  readonly idempotent: boolean;
  /**
   * Whether the operation declares `Idempotency-Key`, which every POST, PATCH and DELETE of the
   * Map Studio API does but `postSession`, `postOrganizationLookup` and the two collaboration
   * signals, `postItineraryCollab` and `postZoneCollab`; the Tile API has no write, and no read
   * declares it. Where it does, each attempt of the call sends one key:
   * {@link RequestArgs.idempotencyKey} where the caller passes one, and otherwise one drawn from
   * the runtime's cryptographic random source before the first attempt, or none where the runtime
   * has no such source. Where it does not, no key is sent, the caller's included.
   */
  readonly takesIdempotencyKey?: boolean | undefined;
  /**
   * The `Idempotency-Key` every attempt of this call sends, as given, on an operation that states
   * {@link RequestArgs.takesIdempotencyKey}; on any other it is neither sent nor read. A request
   * that carries a key is repeated after a 502, a 503, a 504, a dropped connection and a 409
   * saying the key is still in use, whatever the operation declares, since the API answers a key
   * it has seen with the answer it stored, and every error the call raises carries the key it
   * sent. One that is empty, whitespace alone, or holds a character no request header carries is
   * refused before the first attempt.
   */
  readonly idempotencyKey?: string | undefined;
  /**
   * The tag a conditional write names, sent as `If-Match` on every attempt exactly as given: the
   * `etag` of the resource as the API wrote it, or `*` for a resource that exists at all. The
   * client never writes one the caller did not name. A stale one is answered with a 412, a
   * `MountayaPreconditionFailedError`, which is never repeated. One that is empty, whitespace
   * alone, or holds a character no request header carries is refused before the first attempt.
   */
  readonly ifMatch?: string | undefined;
  /**
   * Whether the operation acts as the user its session token was minted for: its
   * `x-acting-user` is `required`. A generated operation states `true` there and nothing
   * elsewhere. Where it is stated, a credential whose {@link SessionTokenSource} reports a token
   * minted for no user is refused locally, since the API would answer the call with a 401.
   */
  readonly actsAsMember?: boolean | undefined;
  /**
   * Whether this operation only READS where its method would say it writes. Omitted means
   * "whatever the method implies", which is right for every REST and tile operation. A share
   * token refused on an operation that does not accept one is told it cannot write where the
   * operation writes.
   *
   * `idempotent` answers a different question and cannot stand in for this one: `PATCH` and
   * `DELETE` are idempotent writes. It belongs on any operation whose verb is the transport rather
   * than the intent: `core/rules/graphql.ts` sets it for the whole Data API, which is GraphQL
   * over POST.
   */
  readonly read?: boolean | undefined;
  /**
   * The query string, one parameter per member. A member that is `undefined` is left out, and
   * that is the only omission: `""` is sent as `key=`, and `false` and `0` as written.
   */
  readonly query?: Record<string, string | number | boolean | undefined> | undefined;
  /**
   * The request body: sent as JSON, or as it is where it is a `FormData`, whose multipart
   * boundary `fetch` writes. A body that cannot be sent as JSON is refused before the first
   * attempt.
   */
  readonly body?: unknown;
  /**
   * The `Accept` header, `application/json` where it is left out. `text/event-stream` is refused
   * before the first attempt: a stream is opened from `@mountaya/client/events`.
   */
  readonly accept?: string | undefined;
  /** Aborts the call wherever it is, as {@link RequestOptions.signal} does. */
  readonly signal?: AbortSignal | undefined;
}

/**
 * isFormData recognises a multipart body the transport must hand to fetch untouched.
 *
 * One public operation takes an uploaded file: `postItineraryPreview`, whose file
 * providers (gpx, geojson, kml, kmz, fit, tcx) are declared `multipart/form-data` while
 * its online providers are declared `application/json`. Both branches are the same
 * operation, so the discriminator has to be the body's own runtime type.
 *
 * The `typeof` guard is not ceremony: `FormData` is global from Node 18 on, but this
 * package supports embedding in runtimes that provide `fetch` through a polyfill, and a
 * bare `instanceof` against a missing global throws a ReferenceError rather than
 * answering false.
 */
const isFormData = (body: unknown): body is FormData =>
  typeof FormData !== "undefined" && body instanceof FormData;

/**
 * encodeBody turns an operation's body into something fetch can send.
 *
 * A FormData rides through as itself. Serializing it would produce the string
 * "[object FormData]" and upload nothing: a failure that reaches the server as a valid
 * request with a nonsense body, so it surfaces as a parse error naming the file format
 * rather than anything pointing back here.
 *
 * @throws `MountayaConfigurationError` naming the operation for a body `JSON.stringify` refuses
 *   (a cycle, a `BigInt`, a `toJSON` that throws), carrying what it threw as its `cause`.
 */
const encodeBody = (body: unknown, operationId: string): BodyInit | undefined => {
  if (body === undefined) {
    return undefined;
  }

  if (isFormData(body)) {
    return body;
  }

  try {
    return JSON.stringify(body);
  } catch (cause) {
    // The first line of what it threw: a runtime spells a cycle's path over several, and the
    // `cause` carries them all.
    const reason =
      cause instanceof Error ? cause.message.split("\n", 1)[0] : `threw ${describeType(cause)}`;

    throw new MountayaConfigurationError(
      `${operationId}: the body cannot be sent as JSON (${reason}). Pass a body JSON.stringify accepts, with no cycle and no BigInt.`,
      { cause },
    );
  }
};

/**
 * BinaryBody is what a non-JSON operation resolves to: the bytes, plus the two headers
 * that say what they are and what to call them.
 *
 * The bytes alone would be under-described. A tile's media type decides whether it is
 * decoded as protobuf or handed to an image decoder, and an export's `filename` is the
 * name the API chose for the file: a `.gpx` named after the itinerary, in the viewer's
 * language. Reconstructing either from the request would mean re-deriving something the
 * response already answered.
 */
export interface BinaryBody {
  /** The body, whole, as it arrived. */
  readonly bytes: ArrayBuffer;
  /** The response's Content-Type, verbatim, when the response carried one. */
  readonly contentType?: string | undefined;
  /** The download filename from Content-Disposition, when the response carried one. */
  readonly filename?: string | undefined;
}

/**
 * StreamDeclaration is what a call made outside this transport declares about the credentials
 * it takes: the flags {@link RequestArgs} carries for a request this client makes itself, and
 * the operation id a refusal names.
 */
type StreamDeclaration = Partial<
  Pick<RequestArgs, "acceptsShareToken" | "actsAsMember" | "operationId" | "requiresSession">
>;

/**
 * StreamSeam is what {@link MountayaTransport.streamSeam} hands a `text/event-stream` opener: the
 * credential ladder, the resolved fetch, the bound on the wait for a response, and a URL resolver
 * over the REST origin.
 *
 * Every stream on the contract is on the REST surface, so the resolver is bound to that one
 * rather than taking a surface. A tile or a GraphQL stream would need it to grow an argument,
 * and neither exists.
 */
export interface StreamSeam {
  /**
   * applyAuth folds this transport's credential into the request's RequestInit.
   *
   * `declaration` is what the stream's operation declares, as {@link RequestArgs} carries it
   * for a request. Every stream on the contract requires a session token and acts as the member
   * it names, so an opener states `{ actsAsMember: true, requiresSession: true }`, and a
   * credential that cannot meet either is refused here, before the stream opens, with the error a
   * request would raise. No stream accepts a share token, and none takes a secret key.
   */
  readonly applyAuth: (
    init: RequestInit,
    method: string,
    declaration?: StreamDeclaration,
  ) => RequestInit;
  /** doFetch is the fetch this transport resolved: the real one, or the caller's double. */
  readonly doFetch: typeof fetch;
  /**
   * report tells the client's {@link ClientOptions.onResponse} of an open's answer, as a request
   * tells it of each attempt's. An open is one attempt, reported as attempt 0: a reader's reopen
   * is an open of its own.
   */
  readonly report: (response: Response, operationId: string) => void;
  /**
   * sessionExpiresAt answers when the session token an open made now carries expires, where the
   * credential's source reports it. The API ends a stream with the credential it was opened
   * with, so a stream that closes at or after this instant is opened again at once.
   */
  readonly sessionExpiresAt: () => Date | undefined;
  /**
   * waitForSession waits, before an open, for a session token the credential's source reports on
   * its way, as a request waits for one, bounded by {@link StreamSeam.timeoutMs} and `signal`. It
   * answers undefined where there is nothing to wait for, so the open is made at once.
   *
   * @throws (as a rejection) the abort reason once `signal` aborts, and a
   *   `MountayaConnectionError` naming the operation once the wait outlasts the bound.
   */
  readonly waitForSession: (operationId: string, signal: AbortSignal) => Promise<void> | undefined;
  /**
   * timeoutMs is how long an open waits for the stream's response, in milliseconds: the client's
   * own {@link ClientOptions.timeoutMs}, as construction resolved it, so the default where the
   * client named none. An opener bounds the wait for the response with it, as one attempt of a
   * request is bounded. It says nothing of the stream once it is open: a stream's silence has a
   * bound of its own, which the opener holds.
   */
  readonly timeoutMs: number;
  /**
   * url resolves one REST path against the origin this transport is pointed at.
   *
   * A resolver rather than the origin itself, because joining the two is not concatenation:
   * a base with a trailing slash, a base carrying a path prefix, and the empty base a
   * same-origin proxy uses all produce a different join, and every generated operation
   * already goes through this one. A stream opener spelling it itself would be the one
   * request that resolves differently from the rest.
   *
   * `operationId` names the stream's operation in a refusal.
   *
   * @throws `MountayaConfigurationError` for a path that resolves outside the base URL, or that
   *   holds an empty segment or a dot segment, as a request's path is refused.
   */
  readonly url: (path: string, operationId?: string) => string;
}

/** The `filename*` parameter's raw value, up to the next parameter. */
const extendedFilenamePattern = /(?:^|;)[ \t]*filename\*[ \t]*=[ \t]*([^;]*)/i;

/** The plain `filename` parameter's raw value: a quoted string with its escapes, or a token. */
const plainFilenamePattern = /(?:^|;)[ \t]*filename[ \t]*=[ \t]*("(?:[^"\\]|\\.)*"|[^;]*)/i;

/** A value of percent-escapes and plain characters only, with no `%` standing alone. */
const percentEncodedPattern = /^(?:%[0-9a-f]{2}|[^%])*$/i;

/**
 * decodeExtendedValue decodes an RFC 8187 extended value, `charset'language'value`, or answers
 * undefined where it cannot.
 *
 * The language tag is skipped: it says what language the name is in, and is no part of the name.
 * UTF-8 is what RFC 8187 asks a sender to write; ISO-8859-1, which RFC 5987 allows as well, is
 * decoded too, a byte to a character. Any other charset, or an escape that does not decode (a
 * stray `%`, bytes that are not UTF-8), answers undefined, and the plain `filename` beside it
 * stands in. A value with no charset at all, which neither grammar allows and some senders
 * write, is read as UTF-8.
 */
const decodeExtendedValue = (raw: string): string | undefined => {
  // Senders quote it now and then though the grammar does not.
  const value = raw.trim().replace(/^"(.*)"$/, "$1");
  const charsetEnd = value.indexOf("'");
  const languageEnd = charsetEnd < 0 ? -1 : value.indexOf("'", charsetEnd + 1);
  const charset = languageEnd < 0 ? "utf-8" : value.slice(0, charsetEnd).toLowerCase();
  const encoded = languageEnd < 0 ? value : value.slice(languageEnd + 1);

  if (!percentEncodedPattern.test(encoded)) {
    return undefined;
  }

  if (charset === "iso-8859-1") {
    return encoded.replace(/%([0-9a-f]{2})/gi, (_escape, byte: string) =>
      String.fromCharCode(Number.parseInt(byte, 16)),
    );
  }

  if (charset !== "utf-8") {
    return undefined;
  }

  try {
    return decodeURIComponent(encoded);
  } catch {
    // Escapes spelling bytes that are not UTF-8.
    return undefined;
  }
};

/**
 * filenameFromContentDisposition reads the download filename out of the header, or answers
 * undefined where it names none. It never throws: a name it cannot read costs the caller the
 * name, never the download.
 *
 * RFC 8187's `filename*` form comes first because it is the one that survives a non-ASCII name:
 * an itinerary called "Traversée des Écrins" is exported with its accents intact there and
 * mangled in the plain `filename` fallback beside it. The plain form stands in when the extended
 * one is absent or cannot be decoded; see {@link decodeExtendedValue}.
 *
 * Parsing this belongs in the SDK. Every application downloading an export needs the name, the
 * header's grammar is not obvious, and the two forms have to be tried in this order, which is
 * three chances to get it subtly wrong, once per integration.
 */
export const filenameFromContentDisposition = (header: string | null): string | undefined => {
  if (header === null || header === "") {
    return undefined;
  }

  const extended = extendedFilenamePattern.exec(header)?.[1];
  const decoded = extended === undefined ? undefined : decodeExtendedValue(extended);
  if (decoded !== undefined && decoded !== "") {
    return decoded;
  }

  const plain = plainFilenamePattern.exec(header)?.[1]?.trim() ?? "";
  const quoted = /^"((?:[^"\\]|\\.)*)"$/.exec(plain)?.[1];
  // A quoted string loses its quotes and its escapes; a token, or a quote left unclosed, is read
  // as written, less any quote at either end.
  const name = quoted === undefined ? plain.replace(/^"|"$/g, "") : quoted.replace(/\\(.)/g, "$1");

  return name === "" ? undefined : name;
};

/** Matches a base URL that carries its own scheme and authority. */
const absoluteUrlPattern = /^[a-z][a-z0-9+.-]*:\/\//i;

/** Matches a string that reads as a host name, with a port or a path perhaps, and no scheme. */
const bareHostPattern = /^(localhost|[a-z0-9-]+(\.[a-z0-9-]+)+)(:\d+)?(\/\S*)?$/i;

/**
 * SAME_ORIGIN_BASE stands in for the current origin while a relative base URL is joined.
 *
 * A relative base (`""` for same-origin, or `/api` behind a reverse proxy) is a real
 * configuration: an application served from the same host as the API, or fronting it
 * with its own gateway, has no absolute URL to name. `new URL` cannot join against one, so
 * the join happens against this placeholder and the placeholder is then removed, which
 * keeps path resolution and query encoding in URL's hands for both shapes.
 *
 * The `.invalid` TLD is reserved by RFC 2606 and can never resolve, so a slip that let one
 * of these escape would fail immediately rather than reaching some real host.
 */
const SAME_ORIGIN_BASE = "http://same-origin.invalid";

/**
 * pageOrigin answers the origin of the page's own address, or undefined where there is none to
 * read a path against: no page at all, as in Node, or an address that names no `http` or `https`
 * origin (`about:blank`, a `file:` document, a worker started from a `blob:`).
 *
 * Read off `location`, which a `<base>` element does not move, and never off the document's base
 * URL, which one does. A relative URL handed to `fetch` is resolved against that base, so on a
 * page whose `<base>` names another host a path in `baseUrls` would send the request there with
 * the client's credential. In a worker `location` is the worker's own address, which is what
 * its `fetch` resolves against.
 */
const pageOrigin = (): string | undefined => {
  // Read against a widened type: `lib.dom` declares a `location` on every runtime, and Node
  // holds none.
  const runtime: { readonly location?: { readonly href?: unknown } | undefined } = globalThis;
  const href = runtime.location?.href;
  if (typeof href !== "string") {
    return undefined;
  }

  let address: URL;
  try {
    address = new URL(href);
  } catch {
    // An address the parser does not read names no origin.
    return undefined;
  }

  return address.protocol === "http:" || address.protocol === "https:" ? address.origin : undefined;
};

/** Matches a path segment the URL parser reads as a dot segment, written or percent-encoded. */
const dotSegmentPattern = /^(?:\.|%2e){1,2}$/i;

/**
 * Matches what the URL parser drops from either end of a reference before it reads it: spaces
 * and the control characters below them.
 */
const edgeControlPattern = /^[\0-\x20]+|[\0-\x20]+$/g;

/**
 * refusePath builds the refusal of a path the join would not send where it is written to.
 * `context` names the operation where the caller has one.
 */
const refusePath = (
  path: string,
  why: string,
  context: string | undefined,
): MountayaConfigurationError =>
  new MountayaConfigurationError(
    `${context === undefined ? "" : `${context}: `}the path ${JSON.stringify(path)} ${why}`,
  );

/**
 * joinUrl joins an operation's path and query onto a base URL and answers the join in the form
 * the base is written in: an absolute URL for an absolute base, a path for a relative one. It
 * reads nothing of the runtime, so the same base and path join to the same value on a page and
 * off one, which is what lets {@link isJoinableBase} judge a base by it.
 *
 * The path is held to the base it is joined onto. The join resolves it as a URL reference, so a
 * path that is an absolute URL, or one that opens with more than one slash, names another origin,
 * and a dot segment or an empty one (which an identifier of `..`, `.` or `""` makes, the first two
 * surviving percent-encoding) addresses another route than the one written. Each is refused: the
 * request would carry the client's credential there. `context` names the operation in the
 * refusal. The segments are read as the join reads them, without the whitespace it removes: a
 * tab or a line break anywhere in the path, and spaces and control characters at its ends.
 *
 * @throws `MountayaConfigurationError` for a path that leaves the base or holds an empty or a dot
 *   segment; `TypeError` where the base does not parse, which {@link isJoinableBase} refuses at
 *   construction so that no request reaches it.
 */
const joinUrl = (
  baseUrl: string,
  path: string,
  query: RequestArgs["query"],
  context?: string,
): string => {
  const absolute = absoluteUrlPattern.test(baseUrl);
  const base = absolute
    ? baseUrl
    : `${SAME_ORIGIN_BASE}${baseUrl.startsWith("/") ? "" : "/"}${baseUrl}`;
  const root = new URL(base.endsWith("/") ? base : `${base}/`);

  // Read as the parser reads it, in the parser's order. It first drops the spaces and control
  // characters at either end and every tab, line feed and carriage return wherever they stand,
  // so a segment written `.`, a tab, `.` is the dot segment `..` by the time it is resolved: the
  // segments are read off the path with those gone, and the join is made from it. Then a
  // backslash is a slash, and the path ends where a query or a fragment begins. One leading
  // slash is the path's own, written either way, and a path of nothing is the base.
  const relative = path
    .replace(edgeControlPattern, "")
    .replace(/[\t\n\r]/g, "")
    .replace(/^[/\\]/, "");
  const written = relative.replace(/[?#].*$/s, "").replaceAll("\\", "/");
  if (
    written !== "" &&
    written.split("/").some((part) => part === "" || dotSegmentPattern.test(part))
  ) {
    throw refusePath(
      path,
      'holds an empty segment or a dot segment ("." or ".."), which addresses another route than the one written. Pass each identifier as the platform issued it.',
      context,
    );
  }

  // Joined behind `./`, so the parser reads what is left as segments under the base and as
  // nothing else: without it a first segment holding a colon reads as a scheme, and one that
  // opens with a space loses it to the parser's second look at the ends. Every path the check
  // above takes stays under the base, and the comparison below holds the join to that.
  const url = new URL(`./${relative}`, root);
  if (!url.href.startsWith(root.href)) {
    throw refusePath(
      path,
      'resolves outside the base URL it is joined onto, so the request would leave the API with the client\'s credential. Pass a path of the API, starting with one "/".',
      context,
    );
  }

  if (query !== undefined) {
    // `undefined` is the omission, and it is the ONLY omission. An empty string is sent as
    // `?key=`, which several endpoints read as a filter matching nothing where the omitted
    // param means unfiltered, so a caller forwarding a cleared form field straight through
    // must fold `""` back to `undefined` itself rather than relying on this loop to.
    // Narrowing it here instead would make an intentional empty value unsendable.
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    }
  }

  // What is left of a relative join opens with the slash the root ends on.
  return absolute ? url.toString() : url.toString().slice(SAME_ORIGIN_BASE.length);
};

/**
 * buildUrl answers the URL a request is sent to: what {@link joinUrl} answers for an absolute
 * base, and for a relative one that path on the page's own origin, as {@link pageOrigin} reads
 * it, or the path alone where no page names an origin.
 *
 * The origin goes on here, after the join and never inside it: a base is judged at construction
 * by the path it joins to, and a path that resolves to `//host` opens with `//` only while no
 * origin stands before it.
 *
 * @throws `MountayaConfigurationError` for a path {@link joinUrl} refuses; `TypeError` where the
 *   base does not parse.
 */
const buildUrl = (
  baseUrl: string,
  path: string,
  query: RequestArgs["query"],
  context?: string,
): string => {
  const joined = joinUrl(baseUrl, path, query, context);

  return absoluteUrlPattern.test(baseUrl) ? joined : `${pageOrigin() ?? ""}${joined}`;
};

/**
 * isJoinableBase reports whether a base URL is one {@link joinUrl} can join a path onto: `""`,
 * a path, or an absolute URL. It answers the same for one base on a page and off one.
 *
 * The test runs the join itself, so a value this accepts is one the join reads the way its
 * caller meant. An absolute URL must be `http:` or `https:`, because that is what `fetch` sends;
 * `ftp:` parses as a URL and fails at the first call. A relative one must start with exactly one
 * `/`, and must still start with exactly one once joined: the join resolves dot segments and
 * reads a backslash as a slash, so `/.//host` and `/\host` come out as `//host`, which `fetch`
 * reads as another origin. Neither may carry a query or a fragment, which the join would put in
 * the middle of every URL it builds, nor whitespace at either end, which the join drops from an
 * absolute URL and sends as `%20` on a path. A base the join cannot parse at all is refused here
 * rather than at the first call.
 */
const isJoinableBase = (base: string): boolean => {
  if (base === "") {
    return true;
  }

  if (base.trim() !== base || base.includes("?") || base.includes("#")) {
    return false;
  }

  const absolute = absoluteUrlPattern.test(base);
  if (!absolute && (!base.startsWith("/") || base.startsWith("//"))) {
    return false;
  }

  let joined: string;
  try {
    joined = joinUrl(base, "/", undefined);
  } catch {
    return false;
  }

  if (!absolute) {
    return !joined.startsWith("//");
  }

  const { host, protocol } = new URL(joined);

  return (protocol === "http:" || protocol === "https:") && host !== "";
};

/**
 * carriesUserinfo reports whether an absolute base URL names a username or a password before its
 * host (`https://user:pass@host`). Such a URL parses, and `fetch` refuses every request built
 * from it. A base the parser refuses carries none here: {@link isJoinableBase} is what refuses it.
 */
const carriesUserinfo = (base: string): boolean => {
  if (!absoluteUrlPattern.test(base)) {
    return false;
  }

  try {
    const { password, username } = new URL(base);

    return username !== "" || password !== "";
  } catch {
    return false;
  }
};

/**
 * resolveBaseUrls lays a client's `baseUrls` over the defaults, entry by entry, refusing an
 * entry {@link isJoinableBase} rejects and one that {@link carriesUserinfo}. A refused entry that
 * holds an `@` is named and never quoted, whichever rule refuses it.
 *
 * Read entry by entry rather than spread, because a spread copies an entry written as
 * `undefined` over the default it was meant to leave alone, and the first call then throws a
 * `TypeError` while joining its URL.
 *
 * @throws `MountayaConfigurationError` for an entry that is not a joinable base, one that names a
 *   username or a password, or a `baseUrls` that is not an object.
 */
const resolveBaseUrls = (baseUrls: BaseUrlOverrides | undefined): Record<Surface, string> => {
  if (baseUrls === undefined) {
    return { ...defaultBaseUrls };
  }

  if (typeof baseUrls !== "object" || baseUrls === null) {
    throw refuseOption(
      "baseUrls",
      baseUrls,
      'pass an object naming a base URL per surface, such as { rest: "http://localhost:8080" }.',
    );
  }

  const entry = (surface: Surface): string => {
    const value: unknown = baseUrls[surface];
    if (value === undefined) {
      return defaultBaseUrls[surface];
    }

    // Named without its value, which holds a password.
    if (typeof value === "string" && carriesUserinfo(value)) {
      throw new MountayaConfigurationError(
        `baseUrls.${surface} carries a username or a password before its host, which fetch refuses in a request's URL; pass the URL without them, and authenticate through the client's credential.`,
      );
    }

    if (typeof value === "string" && isJoinableBase(value)) {
      return value;
    }

    // What stands before an "@" may be a password in a spelling the URL parser reads as no
    // userinfo, or refuses to read at all, so a refused entry that holds one is not quoted.
    if (typeof value === "string" && value.includes("@")) {
      throw new MountayaConfigurationError(
        `baseUrls.${surface} holds an "@" and is no base URL this client can join a path onto, so its value is not quoted: a username or a password may stand before the "@". Pass an http or https URL with no username, password, query or fragment, a path starting with one "/" that does not resolve to "//", or "" for the page's own origin, with no whitespace at either end.`,
      );
    }

    // A bare host is the likeliest slip, and the one whose fix can be spelled out exactly.
    const scheme =
      typeof value === "string" && bareHostPattern.test(value)
        ? ` For a host, add its scheme: "https://${value}".`
        : "";

    throw refuseOption(
      `baseUrls.${surface}`,
      value,
      `pass an http or https URL with no query or fragment, a path starting with one "/" that does not resolve to "//", or "" for the page's own origin, with no whitespace at either end.${scheme}`,
    );
  };

  return { data: entry("data"), rest: entry("rest"), tiles: entry("tiles") };
};

/**
 * resolveTimeout reads a client's `timeoutMs`, refusing one that is not a whole number of
 * milliseconds a timer can honour.
 *
 * `0` is refused rather than read as "no timeout": it would abort every attempt, and a caller
 * wanting no bound on a long call has the call's own `signal` for that.
 *
 * @throws `MountayaConfigurationError` for a value outside 1 to 2,147,483,647 or a fraction.
 */
const resolveTimeout = (timeoutMs: number | undefined, context?: string): number => {
  if (timeoutMs === undefined) {
    return DEFAULT_TIMEOUT_MS;
  }

  if (Number.isInteger(timeoutMs) && timeoutMs >= 1 && timeoutMs <= MAX_TIMER_DELAY_MS) {
    return timeoutMs;
  }

  throw refuseOption(
    "timeoutMs",
    timeoutMs,
    context === undefined
      ? `pass a whole number of milliseconds from 1 to ${MAX_TIMER_DELAY_MS}, or leave it out for the default of ${DEFAULT_TIMEOUT_MS}.`
      : `pass a whole number of milliseconds from 1 to ${MAX_TIMER_DELAY_MS}, or leave it out for the client's own.`,
    context,
  );
};

/**
 * resolveLanguage reads a client's `language`, refusing one that is not a string, one no
 * request header carries (a line break or another control character inside it, or a character
 * past Latin-1) and one a request carries as nothing: an empty string, or whitespace alone.
 * {@link isHeaderValue} and {@link isEmptyHeaderValue} are the rules.
 *
 * Sent as it is, the runtime's `fetch` refuses the first kind of request, and the first call is
 * reported after its retries as a connection failure that tells the reader to check the network.
 * The second kind is sent as an `Accept-Language` with nothing in it, which states no language
 * while the client reports one from `requestLanguage`. A tag's own grammar is not checked: the
 * API reads the header, and falls back where it knows no such language.
 *
 * @throws `MountayaConfigurationError` for a language that is no string, one no request header
 *   carries, or one that is empty or whitespace alone.
 */
const resolveLanguage = (language: string | undefined): string | undefined => {
  // Typed as a string, and still whatever a caller outside the compiler passed.
  const given: unknown = language;
  if (given === undefined) {
    return undefined;
  }

  if (typeof given === "string" && isHeaderValue(given) && !isEmptyHeaderValue(given)) {
    return given;
  }

  throw refuseOption(
    "language",
    given,
    'pass a BCP 47 tag such as "fr" or "pt-BR", with no line break inside it and no character outside Latin-1, or leave it out to send no Accept-Language.',
  );
};

/**
 * resolveFetch reads a client's `fetch`, refusing one that is not a function, and answers the
 * fetch the client calls: the caller's own, or the global one through {@link boundFetch}.
 *
 * A value that cannot be called surfaces otherwise from the first call, as a connection failure
 * retried and reported with a next step about the network.
 *
 * @throws `MountayaConfigurationError` for a `fetch` that is neither a function nor `undefined`.
 */
const resolveFetch = (fetchImpl: typeof fetch | undefined): typeof fetch => {
  const given: unknown = fetchImpl;
  if (given !== undefined && typeof given !== "function") {
    throw refuseOption(
      "fetch",
      given,
      "pass a function that takes what the global fetch takes, or leave it out to call the global one.",
    );
  }

  return boundFetch(fetchImpl);
};

/**
 * boundFetch resolves the fetch implementation a caller issues requests through, keeping the
 * global one callable through a local binding.
 *
 * `fetch` is bound to the global object, and invoking it through a local or parameter binding
 * (which is exactly how this transport holds it, and how a stream opener is handed one) throws
 * "Illegal invocation" in browsers, with a message naming nothing useful. Wrapping the default
 * in a call preserves the binding. An implementation the caller supplied is already a standalone
 * function and is returned untouched, which is what makes a test double, MSW, or request
 * instrumentation work without the wrapper getting in the way.
 *
 * It is exported because a stream is a fetch the caller owns (see {@link MountayaTransport.applyAuth})
 * and needs the same resolution this transport does. It lives in THIS module rather than beside
 * the stream opener so the package keeps naming the global in exactly one file: the property
 * `boundary.test.ts` pins, and the reason the resolution is written once rather than twice.
 *
 * @param fetchImpl - The caller's own fetch, or `undefined` for the global one.
 * @returns `fetchImpl` as given, or a function that calls the global fetch at each call.
 */
export const boundFetch = (fetchImpl?: typeof fetch): typeof fetch =>
  fetchImpl ?? ((input, init) => globalThis.fetch(input, init));

/**
 * AttemptSignal is the signal one attempt is sent under: the caller's own, joined to the
 * attempt's timeout.
 */
interface AttemptSignal {
  /** The signal the attempt's fetch is handed. */
  readonly signal: AbortSignal;
  /** Whether the timeout, rather than the caller, ended the attempt. */
  readonly timedOut: () => boolean;
  /**
   * Ends the attempt's own hold on the request once it has an answer or has failed: the timeout
   * stops, and a caller's signal that was joined by hand is let go.
   */
  readonly release: () => void;
}

/**
 * attemptSignal joins a caller's signal to one attempt's timeout, on any runtime this package
 * supports.
 *
 * The timeout is a plain timer rather than `AbortSignal.timeout`, for two reasons. The attempt
 * has to be able to stop it: it bounds the wait for a response, and a timer that stayed armed
 * through the body read would cut a large export off mid-download, which is a response that
 * arrived. And a transport relying on either static throws a `TypeError` at its first request
 * where the runtime lacks it: `AbortSignal.timeout` is missing on Safari before 16 and
 * `AbortSignal.any` on Safari before 17.4, and React Native before 0.87 lacks both.
 *
 * The caller's signal is joined with `AbortSignal.any` where the runtime has it, which keeps it
 * joined for as long as the response's body is read, with nothing to release. Elsewhere a
 * listener forwards it, and {@link AttemptSignal.release} removes the listener, so a long-lived
 * caller signal does not keep one closure per request. A body read on such a runtime is ended
 * by the read itself, which watches the caller's signal and cancels the body: see
 * {@link readBytes}.
 */
const attemptSignal = (timeoutMs: number, caller: AbortSignal | undefined): AttemptSignal => {
  const controller = new AbortController();
  let timedOut = false;

  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort(namedError("TimeoutError", `no response within ${timeoutMs} ms`));
  }, timeoutMs);

  const stopTimer = () => {
    clearTimeout(timer);
  };

  if (caller === undefined) {
    return { release: stopTimer, signal: controller.signal, timedOut: () => timedOut };
  }

  // Read off the constructor rather than trusted from the type: `lib.dom` declares `any` on every
  // runtime, and the runtimes this fallback is for do not have it.
  const any: unknown = Reflect.get(AbortSignal, "any");
  if (typeof any === "function") {
    return {
      release: stopTimer,
      signal: AbortSignal.any([caller, controller.signal]),
      timedOut: () => timedOut,
    };
  }

  const forward = () => {
    controller.abort(abortReasonOf(caller));
  };

  if (caller.aborted) {
    forward();
  } else {
    caller.addEventListener("abort", forward, { once: true });
  }

  return {
    release: () => {
      stopTimer();
      caller.removeEventListener("abort", forward);
    },
    signal: controller.signal,
    timedOut: () => timedOut,
  };
};

/**
 * refuseEventStream refuses a call that asks for `text/event-stream`, before the first attempt.
 *
 * A feed has no end: the API writes a heartbeat into it for as long as it is open, so a request
 * that reads its body to the end never settles and holds every chunk it has read. The openers of
 * `@mountaya/client/events` read a feed frame by frame, resume it and report how it closed.
 *
 * @throws `MountayaConfigurationError` naming the operation and the opener that reads it.
 */
const refuseEventStream = (args: Pick<RequestArgs, "accept" | "operationId">): void => {
  const asksForStream = (args.accept ?? "")
    .split(",")
    .some((range) => range.split(";")[0]?.trim().toLowerCase() === "text/event-stream");
  if (!asksForStream) {
    return;
  }

  const opener =
    args.operationId === "getAgentRunEvents"
      ? "createAgentTransport"
      : args.operationId === "getItineraryEvents" ||
          args.operationId === "getOrganizationEvents" ||
          args.operationId === "getZoneEvents"
        ? "createCollabTransport"
        : "openSseStream";

  throw new MountayaConfigurationError(
    `${args.operationId} answers an event stream, which has no end for a request to read to. ` +
      `Open it with ${opener} from @mountaya/client/events, which reads it frame by frame.`,
  );
};

/**
 * discardBody cancels the body of a response nobody is going to read, releasing its connection.
 *
 * Not awaited: the cancellation is the runtime's to finish, and a request waiting on it would be
 * waiting on nothing it needs. A body that is already errored, or absent where the runtime does
 * not stream (React Native), has nothing to release, and its refusal is dropped for that reason.
 *
 * Read against a widened type, since `lib.dom` declares a web stream on every `Response`: a
 * `fetch` option built on another stream family answers a body with no `cancel` to call, a Node
 * `Readable` for one, or one whose `cancel` answers no promise. Such a body is left to the
 * `fetch` that answered it, and never fails the call the next attempt answers.
 */
const discardBody = (response: Response): void => {
  const body: { readonly cancel?: (() => unknown) | undefined } | null | undefined = response.body;
  if (typeof body?.cancel !== "function") {
    return;
  }

  try {
    // Resolved first, so a `cancel` that answers nothing, or a thenable, is chained on alike.
    Promise.resolve(body.cancel()).catch(() => undefined);
  } catch {
    // A `cancel` that throws where the web's rejects has released nothing this client can.
  }
};

/**
 * resolveServerBudget reads an operation's own budget, refusing one that is not a whole number of
 * milliseconds a timer can honour once the margin is added to it.
 *
 * @throws `MountayaConfigurationError` naming the call.
 */
const resolveServerBudget = (budget: number | undefined, context: string): number | undefined => {
  if (budget === undefined) {
    return undefined;
  }

  if (Number.isInteger(budget) && budget >= 1 && budget <= MAX_TIMER_DELAY_MS) {
    return budget;
  }

  throw refuseOption(
    "serverBudgetMs",
    budget,
    `pass the operation's x-timeout-ms, a whole number of milliseconds from 1 to ${MAX_TIMER_DELAY_MS}, or leave it out where it declares none.`,
    context,
  );
};

/**
 * attemptBudget answers how long each attempt of a call waits for its response: the call's own
 * `timeoutMs` where it names one, and otherwise the larger of the client's and the operation's
 * `x-timeout-ms` plus {@link SERVER_BUDGET_MARGIN_MS}, held to what a timer honours.
 *
 * @throws `MountayaConfigurationError` for a call budget or an operation budget out of range.
 */
const attemptBudget = (
  clientMs: number,
  args: Pick<RequestArgs, "operationId" | "serverBudgetMs" | "timeoutMs">,
): number => {
  if (args.timeoutMs !== undefined) {
    return resolveTimeout(args.timeoutMs, args.operationId);
  }

  const server = resolveServerBudget(args.serverBudgetMs, args.operationId);

  return server === undefined
    ? clientMs
    : Math.min(MAX_TIMER_DELAY_MS, Math.max(clientMs, server + SERVER_BUDGET_MARGIN_MS));
};

/**
 * assertCallerHeader refuses a value the caller passes for a request header that no request can
 * carry: one that is empty or whitespace alone, or holds a line break, another control character
 * or a character outside Latin-1. Anything else is the API's to judge.
 *
 * @throws `MountayaConfigurationError` naming the call and the member, never the value.
 */
const assertCallerHeader = (
  member: "idempotencyKey" | "ifMatch",
  value: unknown,
  context: string,
): void => {
  if (value === undefined) {
    return;
  }

  if (typeof value === "string" && isHeaderValue(value) && !isEmptyHeaderValue(value)) {
    return;
  }

  throw new MountayaConfigurationError(
    `${context}: ${member} is ${typeof value === "string" ? "a string no request header carries" : describeType(value)}; ` +
      (member === "idempotencyKey"
        ? "pass a key of printable characters, or leave it out for the client to draw one."
        : "pass the etag the resource was read under, as the API wrote it, or leave it out for an unconditional write."),
  );
};

/**
 * surfaceFault hands a hook's own fault to the runtime as an uncaught error, on a microtask: a
 * hook that throws is a defect of the program that passed it, and must neither fail the call
 * nor disappear.
 */
const surfaceFault = (fault: unknown): void => {
  queueMicrotask(() => {
    throw fault;
  });
};

/**
 * resolveHook reads a client's `onResponse`, refusing one that is not a function.
 *
 * @throws `MountayaConfigurationError` for a hook that is neither a function nor `undefined`.
 */
const resolveHook = (
  hook: ClientOptions["onResponse"],
): ((report: ResponseReport) => void) | undefined => {
  const given: unknown = hook;
  if (given === undefined || typeof given === "function") {
    return hook;
  }

  throw refuseOption("onResponse", given, "pass a function told of each answer, or leave it out.");
};

/**
 * firstCodeOf reads the classifying code of a refusal's body, the first entry's, or undefined
 * where the body is no envelope.
 */
const firstCodeOf = (text: string): string | undefined => {
  let body: unknown;
  try {
    body = JSON.parse(text);
  } catch {
    return undefined;
  }

  return entriesOf(body)[0]?.extensions?.code;
};

/**
 * rereadable answers a refusal whose body is already read as a response that can be read again,
 * with the same status and headers, so the error it maps to is built from the same answer.
 */
const rereadable = (response: Response, text: string): Response =>
  new Response(text === "" ? null : text, {
    headers: response.headers,
    status: response.status,
    statusText: response.statusText,
  });

/** SentCall is what one call's retry loop answers: the last response, and the key it sent. */
interface SentCall {
  /** The `Idempotency-Key` every attempt of the call sent, or undefined where it sent none. */
  readonly idempotencyKey: string | undefined;
  /** The response of the call's last attempt, as it arrived, its body unread. */
  readonly response: Response;
}

/**
 * carryingKey answers a failure raised between two attempts of a call that sent a key, as the
 * same failure carrying that key: a configuration error or a connection error the client raised
 * once the first attempt was sent, which may have made the write. Anything else, a caller's
 * abort reason among it, is answered as it is.
 */
const carryingKey = (failure: unknown, idempotencyKey: string | undefined): unknown => {
  if (idempotencyKey === undefined) {
    return failure;
  }

  if (failure instanceof MountayaConfigurationError) {
    return new MountayaConfigurationError(failure.message, {
      cause: failure.cause,
      idempotencyKey,
    });
  }

  if (failure instanceof MountayaConnectionError) {
    return new MountayaConnectionError(failure.message, failure.cause, idempotencyKey);
  }

  return failure;
};

/**
 * MountayaTransport performs authenticated, retrying requests against the three surfaces.
 *
 * Prefixed, like every class this package publishes. `Transport` is one of the most reusable
 * nouns in the language (a consumer's own HTTP layer, a test double, a second vendor's SDK),
 * and an unprefixed published class is a name a consumer has to alias on import the first
 * time two of them meet. It is also the name a stack trace and a `instanceof` failure print,
 * where "Transport" says nothing about whose.
 */
export class MountayaTransport {
  private readonly credential: Credential;
  private readonly baseUrls: Record<Surface, string>;
  private readonly timeoutMs: number;
  private readonly retry: Required<RetryOptions>;
  private readonly language?: string | undefined;
  private readonly doFetch: typeof fetch;
  private readonly credentialsMode: RequestCredentials;
  private readonly onResponse: ((report: ResponseReport) => void) | undefined;

  /**
   * Builds a transport over one credential and its options, checking both before anything is
   * sent.
   *
   * @throws `MountayaConfigurationError` for options that are not an object, a credential that
   *   cannot work, or an option outside its range: see {@link ClientOptions}.
   */
  constructor(options: ClientOptions) {
    // Typed as the options, and still whatever a caller outside the compiler passed: a
    // JavaScript `new Mountaya()`, or a configuration read that came back empty. Read as unknown
    // until it is shown to be an object, so the refusal names the options and the fix rather
    // than a `TypeError` naming `credential`. It names their type and never their value, which
    // may be a key.
    const given: unknown = options;
    if (typeof given !== "object" || given === null) {
      throw new MountayaConfigurationError(
        `options is ${describeType(given)}; pass { credential }, with any other option beside it, such as { credential: { publishableKey } }.`,
      );
    }

    assertCredential(options.credential);

    this.credential = options.credential;
    // Resolved once: whether this credential asks the browser to attach its own cookies. The
    // cookie credential does, and a share credential that states `cookie`. Every other
    // credential sends `omit` and never `same-origin`: a base URL may be the page's own origin
    // ("" or "/api"), where `same-origin` attaches the page's cookies beside a key, and
    // wantsCookies says why a key never rides with one.
    this.credentialsMode = wantsCookies(options.credential) ? "include" : "omit";
    this.baseUrls = resolveBaseUrls(options.baseUrls);
    this.timeoutMs = resolveTimeout(options.timeoutMs);
    this.retry = resolveRetry(defaultRetry, options.retry);
    this.language = resolveLanguage(options.language);
    this.onResponse = resolveHook(options.onResponse);

    // Resolved at CALL time, not here. Capturing globalThis.fetch in the constructor
    // freezes whatever was installed at that moment, so a client built during module
    // evaluation never sees a fetch patched afterwards, which is how MSW, request
    // instrumentation and test stubs all work. A long-lived client would silently keep
    // calling the original.
    //
    // It is invoked as `globalThis.fetch(...)` rather than through a bare reference: an
    // unbound fetch throws "Illegal invocation" in browsers, with a message naming
    // nothing useful.
    this.doFetch = resolveFetch(options.fetch);
  }

  /**
   * mapCredential projects the browser-safe half of this transport's credential, for a
   * renderer that has to authenticate requests it makes for itself.
   *
   * `@mountaya/elements` exists because MapLibre fetches tiles, sprites and glyphs on its own
   * schedule, and a credential belongs in a header: the Tile API reads one from the query too,
   * for a request that cannot set a header, and a key in a URL lands in every log, cache and
   * `Referer` that URL reaches. MapLibre's `transformRequest` is the one place to attach a
   * header to a request MapLibre makes. Without this projection a consumer would have to pass
   * their key twice (once to the client, once to the map), and the second copy would drift from
   * the first the moment a session token rotated.
   *
   * **A secret key is never projected**, and that is the whole point of it being a
   * projection rather than a getter for `this.credential`. A secret key mints session
   * tokens and must never reach a browser; a transport built with one yields an empty
   * credential here, so the mistake produces an unauthenticated map rather than a leak.
   *
   * **The cookie flag rides along, and it is not redundant with the browser's own
   * behaviour.** A browser attaches cookies unasked only to a SAME-ORIGIN request; the Tile
   * API is a separate host (`tiles.mountaya.com` by default), so the flag is what becomes
   * `credentials: "include"` on the far side. Dropping it costs a signed-in share-link visitor
   * what their own account reads: the token still authorizes the shared resource, but the
   * API cannot see who is asking, so a signed-in visitor is served the anonymous projection
   * with nothing failing.
   *
   * Which credentials ask for it is `wantsCookies`, the same predicate `credentialsMode`
   * uses above, so a map and a REST call made by one client cannot disagree about it. A
   * publishable key deliberately never carries one: the cookie's broader session would
   * outrank the scoped key.
   *
   * **Every read answers the session token current at that read.** A token held as a function
   * is called here each time, so a renderer that reads the projection per request sends the
   * token current at that request, as a REST call from the same client does. The projection never
   * waits: while a source has its first token on its way, a read answers the key alone, so a
   * tile a map asks for in that window is the anonymous one, and the tiles it asks for once the
   * token lands carry it.
   *
   * @throws `MountayaConfigurationError` where a session token held as a function or a source
   *   answers neither a string nor `undefined`, or a string without the `sess_` prefix.
   */
  get mapCredential(): {
    /** `true` where the renderer's requests ride the browser's cookies, as the client's do. */
    readonly cookie?: boolean | undefined;
    /** The publishable key, where the credential holds one. */
    readonly publishableKey?: string | undefined;
    /** The session token current at this read, where the credential holds one. */
    readonly sessionToken?: string | undefined;
    /** The share token, where the credential holds one. */
    readonly shareToken?: string | undefined;
  } {
    const credential = this.credential;
    const cookie = wantsCookies(credential) ? { cookie: true } : {};

    // Read by value, as every other reader of the credential reads it, so a member written as
    // `undefined` is as absent here as it is from the request's headers.
    if (isShareCredential(credential)) {
      return { ...cookie, shareToken: credential.shareToken };
    }

    if (isPublishableCredential(credential)) {
      const sessionToken = sessionTokenOf(credential);

      return sessionToken === undefined
        ? { publishableKey: credential.publishableKey }
        : { publishableKey: credential.publishableKey, sessionToken };
    }

    // A secret or cookie credential. The secret cannot go to a browser at all, and
    // `wantsCookies` is false for it, so it projects nothing; the cookie credential has no
    // header to forward and projects the flag alone.
    return cookie;
  }

  /**
   * applyAuth folds this transport's credential into a `RequestInit`, for a call this
   * client does not make itself.
   *
   * Server-sent events are the case it exists for. A stream is a `fetch` the caller owns (it
   * reads the body rather than awaiting it, reconnects on its own terms, and lives far longer
   * than a request), so it cannot go through {@link request}. What it must not own is the
   * credential contract: which header a share token rides in, that a session token
   * accompanies a publishable key rather than replacing it, that a cookie credential sends
   * no header and asks for the cookie instead. A host that rebuilds that ladder gets a
   * ladder that agrees with this one until one of them learns something.
   *
   * It stays narrower than a streaming method although two stream vocabularies ARE on the
   * contract. {@link streamSeam} is what a stream takes from this transport, and the
   * openers over it live on `@mountaya/client/events` rather than here, so a caller reading
   * REST and GraphQL never pays for a reader they do not open.
   *
   * Caller-supplied headers and init survive: this merges into what it is given, so an
   * `Accept: text/event-stream` or a `signal` set by the caller is not replaced. `credentials`
   * is the one member it always writes: `include` for a cookie credential and for a share
   * credential that states `cookie`, and `omit` for every other, so a key or a bare share token
   * never travels beside the page's cookies, whatever origin the API is reached at. The client's
   * `language` rides as `Accept-Language`, as on every request it makes, so a stream's refusals
   * and the names its events carry come in the language the rest of the client reads.
   *
   * The credential is read at each call, a session token held as a function or a source
   * included, so a stream reopened after the token rotated opens with the current one. It reads
   * the token held at the call and never waits for one on its way, where a request does.
   *
   * `declaration` carries what the call's operation declares, as {@link RequestArgs} does for a
   * request this client makes, and the same refusals read it: a secret key, which authenticates
   * no stream, a share token where it does not state `acceptsShareToken`, a credential with no
   * session token where it states `requiresSession`, and a session source reporting a token
   * minted for no user where it states `actsAsMember`. Each is a configuration error thrown here,
   * before anything is opened. The server would answer a share-token write with a 401, or a 400
   * when validation fails first, and neither names the rule.
   */
  applyAuth(
    init: RequestInit = {},
    method = "GET",
    declaration: StreamDeclaration = {},
  ): RequestInit {
    const projected = credentialHeaders(this.credential, { ...declaration, method });

    const headers = new Headers(init.headers);
    for (const [name, value] of Object.entries(projected)) {
      headers.set(name, value);
    }

    if (this.language !== undefined) {
      headers.set("Accept-Language", this.language);
    }

    return { ...init, credentials: this.credentialsMode, headers };
  }

  /**
   * streamSeam projects what one `text/event-stream` open needs from this transport: the
   * credential ladder, the fetch this client resolved, the bound one attempt waits for a
   * response, and the origin the REST surface is being reached at.
   *
   * It exists because a stream cannot go through {@link requestRaw} and the reasons are
   * structural rather than stylistic. `requestRaw` retries a non-ok status, which for a stream
   * means silently reopening a feed the caller believes it holds one handle to, and it accepts
   * no per-call headers, so there is nowhere to put the `Last-Event-ID` a resume rides on.
   * `requestBinary` is further still: it buffers the whole body, which for a feed with no end
   * never resolves.
   *
   * Handing the seam out rather than opening the stream here is what keeps the reader off
   * the root barrel's graph. The openers live on `@mountaya/client/events`, so a consumer
   * reading REST and GraphQL pays nothing for a frame parser they never use, which is the
   * split the entry point itself exists for.
   *
   * `doFetch` is projected rather than kept private because the alternative is worse: the
   * caller resolving its own, which is precisely the bypass `boundary.test.ts` guards
   * against. What comes back here is the fetch this transport already uses, so a test
   * double, an instrumented fetch or a polyfill installed on the client reaches the stream
   * too. Retry is deliberately NOT part of it: reconnection is a stream's own policy, and
   * a transport-level retry would reopen a feed behind the caller's back.
   */
  get streamSeam(): StreamSeam {
    return {
      applyAuth: (init, method, declaration) => this.applyAuth(init, method, declaration),
      doFetch: this.doFetch,
      report: (response, operationId) => {
        this.report(response, { operationId, surface: "rest" }, 0);
      },
      sessionExpiresAt: () => sessionExpiryOf(this.credential),
      timeoutMs: this.timeoutMs,
      url: (path, operationId) => buildUrl(this.baseUrls.rest, path, undefined, operationId),
      waitForSession: (operationId, signal) => {
        const source = sessionSourceOf(this.credential);

        return source === undefined
          ? undefined
          : waitForSessionToken(source, { operationId, signal, timeoutMs: this.timeoutMs });
      },
    };
  }

  /**
   * credentialScope identifies WHICH credential this transport holds and WHO it acts as, for a
   * cache key.
   *
   * One query cache usually serves a whole application, so a resource read under one
   * credential and the same resource read under another land on one entry unless something
   * separates them. That is wrong twice over, and the second way is a security property
   * rather than a correctness one: a share link uses its first read as the token's
   * AUTHORIZATION PROBE, so an entry seeded by a different credential would let a rotated
   * or foreign token resolve out of cache without ever reaching the API.
   *
   * The share token is part of the value for exactly that reason: two successive tokens
   * for one resource are the case the probe exists to separate, and keying on the kind
   * alone would collapse them. A query key is process-local memory and a share token is
   * already in the address bar of the page reading it, so this exposes nothing that was not
   * already visible.
   *
   * Under a publishable key, the session is part of the value, since the session token names the
   * user a request acts as and what a member reads is theirs:
   *
   * - a token held by a source that reports the user it was minted for, as a
   *   `SessionTokenEngine` does, is keyed by that user, `user:<id>`, or as one naming nobody, so
   *   a page whose person signs out or switches account serves nobody what the person before
   *   them read, and a token the source rotates for the same person keeps the entries;
   * - a token held as a string is keyed by the token itself, since the client is not told whom
   *   it names and it never rotates;
   * - a token held as a function, or by a source that reports no user, is keyed by its presence
   *   alone, since its value rotates and folding it in would evict the whole cache every refresh
   *   to distinguish a credential from itself. Present means a request made now carries a token
   *   or waits for one, which a source reports from construction until its mint answers.
   *
   * An answer read under the key alone (a server prefetch, a failed mint, a sign-out) is the
   * anonymous projection, keyed apart from every session. A secret key is named by its kind
   * alone, and the browser's own session as `cookie`. {@link subscribeCredentialScope} tells a
   * listener each time the value moves.
   *
   * Before a source's first token lands, the value names the session's presence and not yet its
   * user, and {@link credentialScopePending} is true: a cache holds its reads until it turns
   * false, so each one is keyed once, under the user the token names.
   *
   * Opaque by contract. Compare it, do not parse it.
   *
   * @throws what a session token held as a function throws, since the function is called here.
   */
  get credentialScope(): string {
    const credential = this.credential;

    if (isShareCredential(credential)) {
      return `share:${credential.shareToken}`;
    }

    if (isSecretCredential(credential)) {
      // A secret key never reaches a browser, so a transport holding one is server-side and
      // there is one per process. Naming the key would put a secret in a cache key.
      return "secret";
    }

    if (!isPublishableCredential(credential)) {
      return "cookie";
    }

    const key = `key:${credential.publishableKey}`;
    if (!holdsSession(credential)) {
      return key;
    }

    const member = credential.sessionToken;
    if (typeof member === "string") {
      return `${key} session:${member}`;
    }

    const user = sessionUserOf(credential);
    if (user === null) {
      return `${key} session nobody`;
    }

    return user === undefined ? `${key} session` : `${key} session user:${user}`;
  }

  /**
   * credentialScopePending reports whether {@link credentialScope} is still to be told who the
   * session acts as: the credential's {@link SessionTokenSource} holds no token and reports one
   * on its way, which it does from its construction until its first mint answers, and again
   * after a sign-out while the next person's mint is out.
   *
   * While it is true, the scope names a session and no user yet, and the token that lands names
   * the user a request made now acts as. That landing refines the scope of the same credential
   * rather than replacing it, so a query cache holds every read keyed by the scope until this
   * turns false, and keys each read once, under the scope the token settles. Holding them costs
   * no time: a request made now waits for that token anyway.
   *
   * False for every other credential and state: a token held as a string or a function, a source
   * that holds a token (a refresh in flight included), a source whose mint failed or that is
   * disabled, a key alone, a secret key, a share token and the browser's own session.
   * {@link subscribeCredentialScope} tells a listener each time it moves.
   */
  get credentialScopePending(): boolean {
    const source = sessionSourceOf(this.credential);
    if (source === undefined) {
      return false;
    }

    const snapshot = source.getSnapshot();

    return snapshot.token === undefined && snapshot.isLoading;
  }

  /**
   * subscribeCredentialScope registers a listener told each time {@link credentialScope} or
   * {@link credentialScopePending} moves, and returns its release.
   *
   * Both move where the credential's {@link SessionTokenSource} does: a first mint that lands, a
   * sign-out that clears the token, a mint for another person. A refresh for the same person
   * moves neither. The listener reads them both, and {@link actsAsMember}, which moves with them.
   * A credential with no source never moves, and its release does nothing. Subscribing holds the
   * source as any subscriber does, so an engine nobody held yet starts its mint. A fault the
   * listener throws reaches the runtime as the program's own uncaught error, on a microtask, and
   * every later move is still told.
   *
   * @param listener - Called with no argument after each move of the scope or of its pending flag.
   * @returns The function that removes the listener.
   */
  subscribeCredentialScope(listener: () => void): () => void {
    const source = sessionSourceOf(this.credential);
    if (source === undefined) {
      return () => undefined;
    }

    let lastScope = this.credentialScope;
    let lastPending = this.credentialScopePending;

    return source.subscribe(() => {
      const scope = this.credentialScope;
      const pending = this.credentialScopePending;
      if (scope === lastScope && pending === lastPending) {
        return;
      }

      lastScope = scope;
      lastPending = pending;
      try {
        listener();
      } catch (fault) {
        surfaceFault(fault);
      }
    });
  }

  /**
   * actsAsMember reports whether a call that acts as a member can be made under this transport's
   * credential now, as far as the client is told: `true` where its session source reports a token
   * minted for a user, `false` where it reports one minted for nobody, and where the credential
   * carries no session token at all (a bare publishable key, a secret key, a share token), and
   * undefined where the client is not told, as under a token held as a string or a function, a
   * source that reports no user, or the browser's own session.
   *
   * It is the question a surface that writes asks to decide whether to offer its writes: `false`
   * means every call that acts as a member is refused before it is sent. Read it again when
   * {@link subscribeCredentialScope} reports a move.
   */
  get actsAsMember(): boolean | undefined {
    const credential = this.credential;

    if (isSecretCredential(credential) || isShareCredential(credential)) {
      return false;
    }

    if (!isPublishableCredential(credential)) {
      return undefined;
    }

    if (credential.sessionToken === undefined) {
      return false;
    }

    const user = sessionUserOf(credential);

    return user === undefined ? undefined : user !== null;
  }

  /**
   * reachesSessionOperations reports whether this transport can make the operations that require
   * a session token: every Data API request, and every `management` and `studio-embedded`
   * operation of the Map Studio API, the writes and the collaboration and agent streams among them.
   *
   * It answers for the credential's shape and never for a token: true under a publishable key
   * given a `sessionToken` in any form (a string, a function or a source, whether or not it holds
   * a token at this moment), and under the browser's own session, which those refusals do not
   * read; false under a publishable key given no session token, a secret key and a share token,
   * each of which is refused such an operation before anything is sent. A function is not called
   * and a source is not read, so a session whose first mint is still out, or whose last mint
   * failed, reaches them here, and the request itself waits for its token or is refused for want
   * of one.
   *
   * It is the question a surface that writes asks at configuration time, where the answer false
   * means none of its writes can ever leave the process.
   */
  get reachesSessionOperations(): boolean {
    const credential = this.credential;

    if (isSecretCredential(credential) || isShareCredential(credential)) {
      return false;
    }

    // Read by value, as every reader of the credential reads it: a member written as `undefined`
    // is an omitted one.
    return isPublishableCredential(credential) ? credential.sessionToken !== undefined : true;
  }

  /**
   * tilesBaseUrl is the origin the Tile API is being reached at.
   *
   * A renderer needs it to decide which of MapLibre's many requests get the credential
   * attached: `transformRequest` is called for every request including third-party
   * basemaps and fonts, so the allowlist is the security boundary, not a nicety. Reading it
   * from here rather than defaulting to the public host is what keeps a map pointed at a
   * local stack or a proxy authenticating correctly.
   */
  get tilesBaseUrl(): string {
    return this.baseUrls.tiles;
  }

  /**
   * requestLanguage is the `Accept-Language` this transport sends, or undefined.
   *
   * The third member of the projection `mapCredential` and `tilesBaseUrl` belong to, and it
   * exists for the same reason they do: tile labels and legends are localized server-side,
   * so a renderer that does not state a language is labelled in whatever the browser
   * happens to prefer while every REST message beside it comes back in the client's. One
   * itinerary, two languages, and nothing failing.
   *
   * A getter rather than a second `language` option on the map, because the two could then
   * disagree, and the one a reader would blame is not the one that would be wrong.
   */
  get requestLanguage(): string | undefined {
    return this.language;
  }

  /**
   * requestRaw performs one call, retrying per the policy, and returns the Response as it
   * arrived, including a non-ok one, with its body unread.
   *
   * It is the single retry loop every other method composes. The Data API needs the raw
   * response because a GraphQL failure arrives as an envelope under the failure's own HTTP
   * status, so classifying by status alone would discard the API's message.
   *
   * Each attempt waits for the response's headers for the call's own `timeoutMs`, or else the
   * larger of the client's and the operation's `serverBudgetMs` plus five seconds. A write that
   * declares `Idempotency-Key` sends one key on every attempt, the caller's or one drawn before
   * the first, and an operation that declares none sends none. Each attempt reads the credential
   * again, so a repeat carries the session token the credential holds when it is sent. A response
   * it repeats has its body cancelled. Every wait between attempts comes out of the call's
   * `retry.maxWaitMs`, and a wait that does not fit is not taken: the response that asked for it
   * comes back as it is. A 409 whose key is still in use is repeated within that budget alone,
   * after at least a second, and not counted against `retry.maxRetries`. Where the runtime has
   * `AbortSignal.any`, the caller's signal reaches the returned body as well; elsewhere a caller
   * reading the body itself cancels it through `response.body`. The client's `onResponse` is told
   * of every attempt an answer came back to.
   *
   * @throws `MountayaConfigurationError` before the first attempt, when the call asks for
   *   `text/event-stream`, the credential cannot make it, its `retry`, `timeoutMs` or
   *   `serverBudgetMs` is out of range, its `idempotencyKey` or `ifMatch` is one no request header
   *   carries, its body cannot be sent as JSON, or its path leaves the base URL or holds an empty
   *   or a dot segment, and once a wait for a session token ends with none on a call that
   *   requires a session, before a repeat as before the first attempt;
   *   `MountayaConnectionError` when no response was obtained at all, saying whether the attempt
   *   timed out, or when a session token the credential's source reports on its way has not
   *   landed within the call's `timeoutMs`, else the client's; {@link abortReasonOf} the caller's
   *   signal once it aborts, whatever `fetch` rejected with. An error raised after the first
   *   attempt was sent carries the `idempotencyKey` the call sent, where it sent one.
   */
  async requestRaw(args: RequestArgs): Promise<Response> {
    return (await this.send(args)).response;
  }

  /**
   * send is {@link requestRaw}, answering the `Idempotency-Key` the call sent beside the response,
   * where it sent one, so the error its answer is read into carries the key a caller sends the
   * call again with.
   */
  private async send(args: RequestArgs): Promise<SentCall> {
    // Every local refusal runs HERE, once, before the loop, never inside it. A refusal is a
    // configuration error: nothing was sent, nothing can be learned by sending it, and the
    // configuration will not have changed by the next attempt. Raised inside the loop's `try`
    // it would read as a request that failed before a response arrived, which is a connection
    // error the loop RETRIES, so a share-token write would surface as a network fault, after a
    // backoff, naming nothing. The first attempt's headers are built here for that reason, and
    // each repeat's again before it is sent.
    refuseEventStream(args);
    const timeoutMs = attemptBudget(this.timeoutMs, args);
    // A key rides an operation that declares the header and no other, so one a caller passes to
    // an operation that declares none is neither sent nor read.
    const takesKey = args.takesIdempotencyKey === true;
    if (takesKey) {
      assertCallerHeader("idempotencyKey", args.idempotencyKey, args.operationId);
    }
    assertCallerHeader("ifMatch", args.ifMatch, args.operationId);

    const url = buildUrl(this.baseUrls[args.surface], args.path, args.query, args.operationId);
    const retry = resolveRetry(this.retry, args.retry, args.operationId);
    // The body is the call's and the same for every attempt, so it is serialized once, and one
    // that cannot be serialized is refused with the other local refusals.
    const body = encodeBody(args.body, args.operationId);

    // A request made while the credential's source holds no token and reports one on its way
    // waits for it, so it carries the session rather than going out under the key alone. Only
    // the operations a session token can ride on wait, and every refusal that does not read the
    // token runs ahead of the wait, so a mint that never answers hides none of them. A repeat
    // waits the same way, for a source that lost its token between two attempts.
    const source =
      args.takesNoCredential === true || args.requiresSecretKey === true
        ? undefined
        : sessionSourceOf(this.credential);
    // Undefined where there is nothing to wait for, so an attempt with no wait is sent in the
    // same turn it is made.
    const waitForSession = (): Promise<void> | undefined =>
      source === undefined
        ? undefined
        : // Bounded by the call's own budget, else the client's: the operation's own is the most
          // the API spends on it, and the mint is the application's endpoint, not the API.
          waitForSessionToken(source, {
            operationId: args.operationId,
            signal: args.signal,
            timeoutMs: args.timeoutMs === undefined ? this.timeoutMs : timeoutMs,
          });
    const firstWait = waitForSession();
    if (firstWait !== undefined) {
      await firstWait;
    }

    // One key for the whole call, drawn once, after every refusal: a call refused locally draws
    // none. A runtime with no cryptographic source draws none, and the call then carries none.
    const idempotencyKey = takesKey ? (args.idempotencyKey ?? mintIdempotencyKey()) : undefined;
    const keyed = idempotencyKey !== undefined;
    let headers = this.headers(args, idempotencyKey);
    // An operation that takes no credential is sent no cookie either, whatever the credential
    // asks for, since a cookie is how the cookie credential and a signed-in share visitor
    // authenticate.
    const credentials: RequestCredentials =
      args.takesNoCredential === true ? "omit" : this.credentialsMode;

    // Every wait of the call comes out of one budget, so a run of refusals cannot hold a call
    // for longer than its caller agreed to.
    let waitedMs = 0;
    // The repeats `retry.maxRetries` counts: every one but the repeat of a key still in use,
    // which asks whether the first attempt has finished and is bounded by the budget alone.
    let retries = 0;

    for (let attempt = 0; ; attempt += 1) {
      if (attempt > 0) {
        // A repeat reads the credential again, so it carries the session token the credential
        // holds as it leaves rather than the one the first attempt carried, which may have
        // expired during the waits between them.
        try {
          const waiting = waitForSession();
          if (waiting !== undefined) {
            await waiting;
          }
          headers = this.headers(args, idempotencyKey);
        } catch (error) {
          throw carryingKey(error, idempotencyKey);
        }
      }

      // A fresh timeout per attempt: the budget is per request, not for the whole
      // retry sequence, so a first attempt that times out does not leave its successors
      // with nothing left.
      const deadline = attemptSignal(timeoutMs, args.signal);

      let response: Response;
      try {
        response = await this.doFetch(url, {
          body,
          credentials,
          headers,
          method: args.method,
          signal: deadline.signal,
        });
      } catch (cause) {
        deadline.release();

        // A caller's own abort is theirs and is never retried or rewrapped. It is read off the
        // signal rather than off what fetch rejected with, because a fetch polyfill may reject an
        // abort with an error of its own (React Native's does), which would lose the reason.
        if (args.signal !== undefined && isAborted(args.signal)) {
          throw abortReasonOf(args.signal);
        }

        // A timeout says so, with the budget and the option that sets it: read as a generic
        // connection failure it sends a reader to the network when the budget may be too short.
        const failure = new MountayaConnectionError(
          deadline.timedOut()
            ? `${args.operationId}: no response within ${timeoutMs} ms. Raise timeoutMs for a call that is slow to answer, or check the network path to the API.`
            : `${args.operationId}: the request failed before a response arrived. Check the network, and that baseUrls.${args.surface} names a host that answers.`,
          cause,
          idempotencyKey,
        );

        if (
          !shouldRetry({
            idempotent: args.idempotent,
            keyed,
            maxRetries: retry.maxRetries,
            retries,
          })
        ) {
          throw failure;
        }

        const delay = jitteredDelay({
          attempt,
          baseDelayMs: retry.baseDelayMs,
          maxDelayMs: retry.maxDelayMs,
        });
        if (waitedMs + delay > retry.maxWaitMs) {
          throw failure;
        }

        waitedMs += delay;
        retries += 1;
        await sleep(delay, args.signal);

        continue;
      }

      // The headers are in, so the timeout has done its work: from here a success's body is
      // read for as long as it takes, and only the caller's signal ends it. A refusal's is read
      // under bounds of its own, see `readFailure`.
      deadline.release();
      this.report(response, args, attempt);

      if (response.ok) {
        return { idempotencyKey, response };
      }

      // A 409 is repeated only where its entry says the key is still in use, so a keyed one is
      // read here, once, under the bounds a refusal's body is read under, and handed on rebuilt
      // from what was read where it is not repeated.
      let code: string | undefined;
      if (response.status === 409 && keyed) {
        const text = await readFailureText(response, args.signal);
        code = firstCodeOf(text);
        response = rereadable(response, text);
      }

      const answered = { code, idempotent: args.idempotent, keyed, status: response.status };
      if (!shouldRetry({ ...answered, maxRetries: retry.maxRetries, retries })) {
        return { idempotencyKey, response };
      }

      // Retry-After is read here rather than left to the caller, because the decision to
      // wait belongs to the loop that is about to wait. Only the headers are read. A key in use
      // waits at least a second, so the budget, its one bound, is spent by every repeat of it.
      const inUse = isKeyInUse(answered);
      const scheduled = backoffDelay({
        attempt,
        baseDelayMs: retry.baseDelayMs,
        maxDelayMs: retry.maxDelayMs,
        retryAfterSeconds: parseRetryAfter(response.headers.get("Retry-After")),
      });
      const delay = inUse ? Math.max(KEY_IN_USE_MIN_WAIT_MS, scheduled) : scheduled;

      // A wait the budget has no room for is not taken: the response goes back as it is, and
      // a 429's error carries the wait for the caller to schedule a retry from.
      if (waitedMs + delay > retry.maxWaitMs) {
        return { idempotencyKey, response };
      }

      // The response being retried is never read, and a body nobody reads holds its connection
      // until the runtime collects it, so under a burst of retries the pool runs dry. Cancelled
      // rather than drained: a drain would download a body only to throw it away.
      discardBody(response);

      waitedMs += delay;
      if (!inUse) {
        retries += 1;
      }
      await sleep(delay, args.signal);
    }
  }

  /**
   * request performs one call and returns the parsed body, with each instant `args.dates` names
   * decoded to a `Date` and every other string left a string.
   *
   * `T` is asserted rather than validated. The generated operation types come from the
   * same spec the API serves, so a runtime re-validation would spend time proving
   * something the contract already guarantees, and would reject a response the moment
   * the API added a field, which is a compatible change.
   *
   * @throws what {@link requestRaw} throws; the `MountayaApiError` class a non-2xx status maps
   *   to; `MountayaConnectionError` for a body that broke off before it was complete, or for a
   *   success whose body is not JSON; the caller's own abort reason while a body, a success's or
   *   a failure's, is still arriving. Each error but the abort reason carries the
   *   `idempotencyKey` the call sent, where it sent one.
   */
  async request<T>(args: RequestArgs): Promise<T> {
    const { idempotencyKey, response } = await this.send(args);
    // The key the call sent, rather than the one the caller passed: an operation that declares
    // none sent none, and a write the caller passed none to sent the one the client drew.
    const call = { ...args, idempotencyKey };

    if (!response.ok) {
      throw await readFailure(response, call);
    }

    const text = await readText(response, call);

    // The wire's boundary: the body is typed by the operation that asked for it, as the
    // contract generates it, and is not validated against that type here.
    return decodeDates(parseSuccessBody(text, response.status, call), args.dates) as T;
  }

  /**
   * requestBinary performs one call and returns the bytes with the two headers that
   * describe them, for tile and export responses that are protobuf, PNG, WebP or an
   * octet stream rather than JSON.
   *
   * It goes through the same retry loop as every other call, so tile fetches, the surface most
   * likely to hit a quota at 600 requests per minute across a whole organization, back off like
   * the rest.
   *
   * @throws as {@link request} does, except for a success whose body is not JSON: the bytes are
   *   handed back as they arrived, with the `Content-Type` that says what they are.
   */
  async requestBinary(args: RequestArgs): Promise<BinaryBody> {
    const { idempotencyKey, response } = await this.send(args);
    const call = { ...args, idempotencyKey };

    if (!response.ok) {
      throw await readFailure(response, call);
    }

    return {
      bytes: await readBytes(response, call),
      contentType: response.headers.get("Content-Type") ?? undefined,
      filename: filenameFromContentDisposition(response.headers.get("Content-Disposition")),
    };
  }

  /**
   * headers builds the headers of one attempt, and is where every local credential refusal is
   * raised: {@link credentialHeaders} reads what the operation declares off `args`, which
   * carries the same members.
   *
   * Built for the first attempt before the retry loop, where a refusal leaves nothing sent, and
   * again for each repeat, so a session token held as a function or a source is read for every
   * attempt: a repeat carries the token the credential holds as it leaves, never one that expired
   * during the waits before it. The call's key and its tag ride every attempt alike.
   */
  private headers(args: RequestArgs, idempotencyKey: string | undefined): Record<string, string> {
    const operation: OperationCredentials = args;
    const headers: Record<string, string> = {
      Accept: args.accept ?? "application/json",
      ...credentialHeaders(this.credential, operation),
    };

    // A multipart body sets no Content-Type here on purpose. fetch derives it from the
    // FormData, including the generated boundary, and any value written here replaces
    // that one, leaving the server a multipart document it cannot split.
    if (args.body !== undefined && !isFormData(args.body)) {
      headers["Content-Type"] = "application/json";
    }

    if (this.language !== undefined) {
      headers["Accept-Language"] = this.language;
    }

    if (idempotencyKey !== undefined) {
      headers["Idempotency-Key"] = idempotencyKey;
    }

    if (args.ifMatch !== undefined) {
      headers["If-Match"] = args.ifMatch;
    }

    return headers;
  }

  /**
   * report tells the client's `onResponse` of one answer. A fault the hook throws leaves the call
   * as it was and reaches the runtime on a microtask.
   */
  private report(
    response: Response,
    call: Pick<RequestArgs, "operationId" | "surface">,
    attempt: number,
  ): void {
    if (this.onResponse === undefined) {
      return;
    }

    try {
      this.onResponse({
        attempt,
        operationId: call.operationId,
        replayed: replayedOf(response.headers),
        status: response.status,
        surface: call.surface,
        ...answerDetailsOf(response.headers),
      });
    } catch (fault) {
      surfaceFault(fault);
    }
  }
}
